/**
 * Device Configuration Form Component
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  NetworkWifi as NetworkIcon,
  Memory as MemoryIcon,
  Tune as TuneIcon,
  Satellite as SatelliteIcon,
  Info as InfoIcon,
  Flight as FlightIcon,
} from '@mui/icons-material';

import {
  DeviceConfigCreate,
  CONFIG_VALIDATION,
  GNSS_DESCRIPTIONS,
  DEFAULT_INTERNAL_DELAYS,
  DEFAULT_CABLE_DELAYS,
  RECEIVER_SIGNALS,
  isValidCalId,
  isInRange,
} from '../../types/config';
import { LEVELS, normalizeForLevel } from '../../types/levels';
import { calculatePIGains } from '../../utils/piLoopCalculator';
import ExcludedMasters from './ExcludedMasters';

type PICalcField =
  | 'pi_tau_n_minutes'
  | 'pi_zeta'
  | 'pi_tau3_ratio'
  | 'pi_fo_hz'
  | 'pi_y_step_hz';

interface ConfigFormProps {
  deviceId: number;
  formData: DeviceConfigCreate;
  onChange: (data: DeviceConfigCreate) => void;
  disabled?: boolean;
}

const ConfigForm: React.FC<ConfigFormProps> = ({ deviceId, formData, onChange, disabled = false }) => {
  // Bind directly to formData to avoid drift/reset on navigation
  
  const handleFieldChange = (field: keyof DeviceConfigCreate, value: any) => {
    onChange({
      ...formData,
      [field]: value,
    });
  };

  // Update a PI loop calculator input AND recompute pi_kp / pi_ki in one go.
  // Last-write-wins: any manual override of Kp/Ki is overwritten as soon as a
  // calculator input changes. The user can still tweak Kp/Ki manually after.
  const handleCalcInputChange = (field: PICalcField, value: number) => {
    const next = { ...formData, [field]: value };
    const { kp, ki } = calculatePIGains({
      tau_n_minutes: next.pi_tau_n_minutes,
      zeta: next.pi_zeta,
      tau3_ratio: next.pi_tau3_ratio,
      fo_hz: next.pi_fo_hz,
      y_step_hz: next.pi_y_step_hz,
    });
    onChange({ ...next, pi_kp: kp, pi_ki: ki });
  };

  const handleGnssCountChange = (newCount: number) => {
    // Ensure every per-receiver array matches the new count
    const currentInternalDelays = formData.gnss_internal_delays || [];
    const currentCableDelays = formData.gnss_cable_delays || [];
    const currentCggttsCab = formData.cggtts_cab_dly || [];
    const currentCggttsInt = formData.cggtts_int_dly || [];

    const newInternalDelays = Array.from({ length: newCount }, (_, i) =>
      currentInternalDelays[i] !== undefined
        ? currentInternalDelays[i]
        : DEFAULT_INTERNAL_DELAYS[i] || 0
    );

    const newCableDelays = Array.from({ length: newCount }, (_, i) =>
      currentCableDelays[i] !== undefined
        ? currentCableDelays[i]
        : DEFAULT_CABLE_DELAYS[i] || 0
    );

    // The server rejects CGGTTS lists longer than gnss_count: trim and pad here
    const newCggttsCab = Array.from({ length: newCount }, (_, i) => currentCggttsCab[i] ?? 0);
    const newCggttsInt = Array.from({ length: newCount }, (_, i) =>
      currentCggttsInt[i] ? [...currentCggttsInt[i]] : [0, 0]
    );

    onChange({
      ...formData,
      gnss_count: newCount,
      gnss_internal_delays: newInternalDelays,
      gnss_cable_delays: newCableDelays,
      cggtts_cab_dly: newCggttsCab,
      cggtts_int_dly: newCggttsInt,
    });
  };

  const handleInternalDelayChange = (index: number, value: number) => {
    const newDelays = [...formData.gnss_internal_delays];
    newDelays[index] = value;
    handleFieldChange('gnss_internal_delays', newDelays);
  };

  const handleCableDelayChange = (index: number, value: number) => {
    const newDelays = [...formData.gnss_cable_delays];
    newDelays[index] = value;
    handleFieldChange('gnss_cable_delays', newDelays);
  };

  const handleAdsbInternalDelayChange = (value: number) => {
    handleFieldChange('adsb_internal_delay', value);
  };

  const handleAdsbCableDelayChange = (value: number) => {
    handleFieldChange('adsb_cable_delay', value);
  };

  const handleExtRefInternalDelayChange = (value: number) => {
    handleFieldChange('ext_ref_internal_delay', value);
  };

  const handleExtRefCableDelayChange = (value: number) => {
    handleFieldChange('ext_ref_cable_delay', value);
  };

  const handleCggttsCabChange = (index: number, value: number) => {
    const next = Array.from({ length: formData.gnss_count }, (_, i) => formData.cggtts_cab_dly?.[i] ?? 0);
    next[index] = value;
    handleFieldChange('cggtts_cab_dly', next);
  };

  const handleCggttsIntChange = (index: number, signal: 0 | 1, value: number) => {
    const next = Array.from({ length: formData.gnss_count }, (_, i) =>
      formData.cggtts_int_dly?.[i] ? [...formData.cggtts_int_dly[i]] : [0, 0]
    );
    next[index][signal] = value;
    handleFieldChange('cggtts_int_dly', next);
  };

  const cggttsDelayError = (value: number | undefined) =>
    !isInRange(value ?? 0, CONFIG_VALIDATION.cggtts_delay);

  return (
    <Box>
      {/* Device Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Device Configuration
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Level selector (styled like OCXO Control Type) */}
            <Grid item xs={12}>
              <FormControl fullWidth disabled={disabled}>
                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Level</FormLabel>
                <ToggleButtonGroup
                  value={`Level ${formData.level ?? 2}`}
                  exclusive
                  onChange={(_, value) => {
                    if (!value) return;
                    const lvl = parseInt(String(value).replace('Level ', ''));
                    if (!Number.isNaN(lvl)) {
                      // The level limits the modes: move to an allowed mode, drop exclusions outside L2
                      onChange(normalizeForLevel({ ...formData, level: lvl }));
                    }
                  }}
                  aria-label="Level"
                  fullWidth
                >
                  {LEVELS.map((lvl) => (
                    <ToggleButton key={lvl} value={`Level ${lvl}`} aria-label={`Level ${lvl}`}>
                      Level {lvl}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <FormHelperText>Select the operating level first: it limits the steering modes (default Level 2)</FormHelperText>
              </FormControl>
            </Grid>

            {formData.level === 2 && (
              <Grid item xs={12}>
                <ExcludedMasters
                  deviceId={deviceId}
                  value={formData.adsb_excluded_master_ids ?? []}
                  onChange={(ids) => handleFieldChange('adsb_excluded_master_ids', ids)}
                  disabled={disabled}
                />
              </Grid>
            )}

            {/* GNSS Antenna Coordinates */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                GNSS Antenna Coordinates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Latitude (GNSS Ant.)"
                    type="number"
                    value={formData.latitude_gnss_ant}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) handleFieldChange('latitude_gnss_ant', v);
                    }}
                    disabled={disabled}
                    inputProps={{ step: 0.00000000000001 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Longitude (GNSS Ant.)"
                    type="number"
                    value={formData.longitude_gnss_ant}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) handleFieldChange('longitude_gnss_ant', v);
                    }}
                    disabled={disabled}
                    inputProps={{ step: 0.00000000000001 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Height (GNSS Ant.)"
                    type="number"
                    value={formData.height_gnss_ant_m}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) handleFieldChange('height_gnss_ant_m', v);
                    }}
                    disabled={disabled}
                    inputProps={{ step: 0.1 }}
                    helperText="meters"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* ADS-B Antenna Coordinates */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                ADS-B Antenna Coordinates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Latitude (ADS-B Ant.)"
                    type="number"
                    value={formData.latitude_adsb_ant}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) handleFieldChange('latitude_adsb_ant', v);
                    }}
                    disabled={disabled}
                    inputProps={{ step: 0.00000000000001 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Longitude (ADS-B Ant.)"
                    type="number"
                    value={formData.longitude_adsb_ant}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) handleFieldChange('longitude_adsb_ant', v);
                    }}
                    disabled={disabled}
                    inputProps={{ step: 0.00000000000001 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Height (ADS-B Ant.)"
                    type="number"
                    value={formData.height_adsb_ant_m}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!Number.isNaN(v)) handleFieldChange('height_adsb_ant_m', v);
                    }}
                    disabled={disabled}
                    inputProps={{ step: 0.1 }}
                    helperText="meters"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* is_adsb_enabled is now automatically calculated based on steering_mode */}
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Slots"
                type="number"
                value={formData.num_slots}
                onChange={(e) => handleFieldChange('num_slots', parseInt(e.target.value) || 0)}
                disabled={disabled}
                inputProps={{
                  min: CONFIG_VALIDATION.num_slots.min,
                  max: CONFIG_VALIDATION.num_slots.max,
                }}
                helperText={`Range: ${CONFIG_VALIDATION.num_slots.min}-${CONFIG_VALIDATION.num_slots.max}`}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* OCXO Control Configuration (moved below Device Configuration) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <NetworkIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              OCXO Control Configuration
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={disabled}>
                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>OCXO Control Type</FormLabel>
                <ToggleButtonGroup
                  value={formData.ocxo_control_type}
                  exclusive
                  onChange={(_, value) => value && handleFieldChange('ocxo_control_type', value)}
                  aria-label="OCXO Control Type"
                  fullWidth
                >
                  <ToggleButton value="SPI" aria-label="SPI">
                    SPI
                  </ToggleButton>
                  <ToggleButton value="SSH" aria-label="SSH">
                    SSH
                  </ToggleButton>
                </ToggleButtonGroup>
                <FormHelperText>Select the control interface for OCXO device</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PPS Offset (ns)"
                type="number"
                value={formData.pps_offset_ns}
                onChange={(e) => handleFieldChange('pps_offset_ns', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                inputProps={{ step: 0.1 }}
                helperText="Negative = Anticipation (advance), Positive = Delay"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="OCXO Control Baseline"
                type="number"
                value={formData.ocxo_control_baseline}
                onChange={(e) => handleFieldChange('ocxo_control_baseline', parseInt(e.target.value) || 0)}
                disabled={disabled}
                inputProps={{
                  min: CONFIG_VALIDATION.ocxo_control_baseline.min,
                  max: CONFIG_VALIDATION.ocxo_control_baseline.max,
                  step: 1,
                }}
                helperText={`DAC code (${CONFIG_VALIDATION.ocxo_control_baseline.min}-${CONFIG_VALIDATION.ocxo_control_baseline.max})`}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>



      {/* GNSS Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <SatelliteIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              GNSS Configuration
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* GNSS Count */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of GNSS Inputs"
                type="number"
                value={formData.gnss_count}
                onChange={(e) => handleGnssCountChange(parseInt(e.target.value) || 1)}
                disabled={disabled}
                inputProps={{
                  min: CONFIG_VALIDATION.gnss_count.min,
                  max: CONFIG_VALIDATION.gnss_count.max,
                }}
                helperText={`Range: ${CONFIG_VALIDATION.gnss_count.min}-${CONFIG_VALIDATION.gnss_count.max} GNSS inputs`}
              />
            </Grid>

            {/* GNSS Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                GNSS Delay Configuration (nanoseconds)
              </Typography>
              
              <Grid container spacing={2}>
                {Array.from({ length: formData.gnss_count }, (_, index) => (
                  <Grid item xs={12} md={6} lg={4} key={index}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 2 
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={1.5}>
                        <SatelliteIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            GNSS{index + 1}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {GNSS_DESCRIPTIONS[index] || `GNSS Input ${index + 1}`}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Internal Delay"
                            type="number"
                            size="small"
                            value={formData.gnss_internal_delays[index] || 0}
                            onChange={(e) => handleInternalDelayChange(index, parseFloat(e.target.value) || 0)}
                            disabled={disabled}
                            inputProps={{
                              step: 0.0001,
                            }}
                            helperText="ns"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Cable Delay"
                            type="number"
                            size="small"
                            value={formData.gnss_cable_delays[index] || 0}
                            onChange={(e) => handleCableDelayChange(index, parseFloat(e.target.value) || 0)}
                            disabled={disabled}
                            inputProps={{
                              step: 0.0001,
                            }}
                            helperText="ns (default: 0.0)"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Delays Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <FlightIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Delays Configuration
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Delay Configuration (nanoseconds)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider',
                      borderRadius: 2 
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={1.5}>
                      <FlightIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          ADS-B Delays
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Internal and cable delay configuration
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Internal Delay"
                          type="number"
                          size="small"
                          value={formData.adsb_internal_delay}
                          onChange={(e) => handleAdsbInternalDelayChange(parseFloat(e.target.value) || 0)}
                          disabled={disabled}
                          inputProps={{
                            step: 0.0001,
                          }}
                          helperText="ns"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Cable Delay"
                          type="number"
                          size="small"
                          value={formData.adsb_cable_delay}
                          onChange={(e) => handleAdsbCableDelayChange(parseFloat(e.target.value) || 0)}
                          disabled={disabled}
                          inputProps={{
                            step: 0.0001,
                          }}
                          helperText="ns (default: 0.0)"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider',
                      borderRadius: 2 
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={1.5}>
                      <NetworkIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          External Reference Delays
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Internal and cable delay configuration
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Internal Delay"
                          type="number"
                          size="small"
                          value={formData.ext_ref_internal_delay}
                          onChange={(e) => handleExtRefInternalDelayChange(parseFloat(e.target.value) || 0)}
                          disabled={disabled}
                          inputProps={{
                            step: 0.0001,
                          }}
                          helperText="ns: external_reference steering only, not used by CGGTTS"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Cable Delay"
                          type="number"
                          size="small"
                          value={formData.ext_ref_cable_delay}
                          onChange={(e) => handleExtRefCableDelayChange(parseFloat(e.target.value) || 0)}
                          disabled={disabled}
                          inputProps={{
                            step: 0.0001,
                          }}
                          helperText="ns (default: 0.0): external_reference steering only, not used by CGGTTS"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* CGGTTS: calibration delays (the preset decides whether CGGTTS steers) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <SatelliteIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              CGGTTS
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="REF_DLY"
                type="number"
                size="small"
                value={formData.cggtts_ref_dly ?? 0}
                onChange={(e) => handleFieldChange('cggtts_ref_dly', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                inputProps={{ step: 0.1, min: CONFIG_VALIDATION.cggtts_ref_dly.min, max: CONFIG_VALIDATION.cggtts_ref_dly.max }}
                error={!isInRange(formData.cggtts_ref_dly ?? 0, CONFIG_VALIDATION.cggtts_ref_dly)}
                helperText="ns, signed, [-1000, 1000]: system PPS pin to receiver EXT_INT pin. Not the external reference delay."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CAL_ID"
                size="small"
                value={formData.cggtts_cal_id ?? 'NA'}
                onChange={(e) => handleFieldChange('cggtts_cal_id', e.target.value)}
                disabled={disabled}
                inputProps={{ maxLength: CONFIG_VALIDATION.cggtts_cal_id_max_length }}
                error={!isValidCalId(formData.cggtts_cal_id ?? '')}
                helperText="Calibration identifier written in the CGGTTS header, no spaces, max 32 characters. NA if not calibrated."
              />
            </Grid>

            {Array.from({ length: formData.gnss_count }, (_, i) => {
              const signals = RECEIVER_SIGNALS[i]?.signals ?? ['Signal 1', 'Signal 2'];
              const intPair = formData.cggtts_int_dly?.[i] ?? [0, 0];
              const cab = formData.cggtts_cab_dly?.[i] ?? 0;
              return (
                <Grid item xs={12} key={`cggtts-rx-${i}`}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    GNSS{i + 1}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="CAB_DLY"
                        type="number"
                        size="small"
                        value={cab}
                        onChange={(e) => handleCggttsCabChange(i, parseFloat(e.target.value) || 0)}
                        disabled={disabled}
                        inputProps={{ step: 0.1, min: 0, max: 1000 }}
                        error={cggttsDelayError(cab)}
                        helperText="ns, [0, 1000]"
                      />
                    </Grid>
                    {([0, 1] as const).map((s) => (
                      <Grid item xs={12} md={4} key={`cggtts-int-${i}-${s}`}>
                        <TextField
                          fullWidth
                          label={`INT_DLY ${signals[s]}`}
                          type="number"
                          size="small"
                          value={intPair[s] ?? 0}
                          onChange={(e) => handleCggttsIntChange(i, s, parseFloat(e.target.value) || 0)}
                          disabled={disabled}
                          inputProps={{ step: 0.1, min: 0, max: 1000 }}
                          error={cggttsDelayError(intPair[s])}
                          helperText="ns, [0, 1000]"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Controller Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <TuneIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Controller Configuration
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="controller-type-label">Controller Type</InputLabel>
                <Select
                  value={formData.controller_type}
                  // The steering mode imposes the controller (the server overrides any other value)
                  disabled
                  label="Controller Type"
                  labelId="controller-type-label"
                >
                  <MenuItem value="kalman">Kalman Filter</MenuItem>
                  <MenuItem value="pi">PI Controller</MenuItem>
                </Select>
                <FormHelperText>Set by the steering mode</FormHelperText>
              </FormControl>
            </Grid>

            {/* PI Controller Settings */}
            {formData.controller_type === 'pi' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="PI Loop Calculator" />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined">
                    Adjust the loop parameters below: Kp and Ki are recomputed automatically.
                    Only Kp and Ki are sent to the device on deploy.
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Natural time constant τₙ (minutes)"
                    type="number"
                    value={formData.pi_tau_n_minutes}
                    onChange={(e) => handleCalcInputChange('pi_tau_n_minutes', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_tau_n_minutes.min,
                      step: 0.1,
                    }}
                    helperText="Loop bandwidth knob — larger = slower, smoother loop"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Damping ratio ζ"
                    type="number"
                    value={formData.pi_zeta}
                    onChange={(e) => handleCalcInputChange('pi_zeta', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_zeta.min,
                      max: CONFIG_VALIDATION.pi_zeta.max,
                      step: 0.1,
                    }}
                    helperText="1.0 = critically damped (recommended)"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Pre-filter ratio τ₃/τₙ"
                    type="number"
                    value={formData.pi_tau3_ratio}
                    onChange={(e) => handleCalcInputChange('pi_tau3_ratio', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_tau3_ratio.min,
                      max: CONFIG_VALIDATION.pi_tau3_ratio.max,
                      step: 0.01,
                    }}
                    helperText="EWMA pre-filter (default 1/6)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nominal frequency F₀ (Hz)"
                    type="number"
                    value={formData.pi_fo_hz}
                    onChange={(e) => handleCalcInputChange('pi_fo_hz', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_fo_hz.min,
                      step: 1,
                    }}
                    helperText="OCXO nominal output frequency"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="DAC step Y_step (Hz/LSB)"
                    type="number"
                    value={formData.pi_y_step_hz}
                    onChange={(e) => handleCalcInputChange('pi_y_step_hz', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_y_step_hz.min,
                      step: 1e-7,
                    }}
                    helperText="Frequency change per 1 LSB of DAC control"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Computed Gains" color="primary" />
                  </Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Proportional Gain (Kp)"
                    type="number"
                    value={formData.pi_kp}
                    onChange={(e) => handleFieldChange('pi_kp', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_kp.min,
                      step: 0.000001,
                    }}
                    helperText="Auto-computed. Manual edits will be overwritten if any calculator input changes."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Integral Gain (Ki)"
                    type="number"
                    value={formData.pi_ki}
                    onChange={(e) => handleFieldChange('pi_ki', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.pi_ki.min,
                      step: 0.000001,
                    }}
                    helperText="Auto-computed. Manual edits will be overwritten if any calculator input changes."
                  />
                </Grid>
              </>
            )}

            {/* Kalman Filter Settings */}
            {formData.controller_type === 'kalman' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Kalman Filter Settings" />
                  </Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Input Window Size"
                    type="number"
                    value={formData.kalman_window_size}
                    onChange={(e) => handleFieldChange('kalman_window_size', parseInt(e.target.value) || 0)}
                    disabled={disabled}
                    inputProps={{
                      min: CONFIG_VALIDATION.kalman_window_size.min,
                      max: CONFIG_VALIDATION.kalman_window_size.max,
                    }}
                    helperText={`Number of samples to average (${CONFIG_VALIDATION.kalman_window_size.min}-${CONFIG_VALIDATION.kalman_window_size.max})`}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Notes */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Additional Notes
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            label="Configuration Notes"
            value={formData.notes || ''}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            disabled={disabled}
            multiline
            rows={4}
            placeholder="Add any additional notes about this configuration..."
            inputProps={{
              maxLength: CONFIG_VALIDATION.notes_max_length,
            }}
            helperText={`${(formData.notes || '').length}/${CONFIG_VALIDATION.notes_max_length} characters`}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ConfigForm; 